import {SelectField} from '@wandb/components';
import {type ItemRowDecorationLeftProps} from '@wandb/components/dist/components/ItemRow';
import React from 'react';

import {envIsPublicCloud, envIsServerDeployment} from '../../../../../config';
import {getModeConfig} from '../../../../PanelMediaBrowser/modes/getModeConfig';
import {
  useMediaActualSizeDisplay,
  useMediaAlwaysOriginalDisplay,
  useMediaFitToDimensionDisplay,
  useMediaModeDisplay,
} from '../../hooks';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsContext} from '../contexts/MediaSettingsContextProvider';

/** Keys for the mutually exclusive image display modes (subset of panel config). */
export const IMAGE_FIT_AND_RESOLUTION_CONFIG_FIELDS = [
  'actualSize',
  'fitToDimension',
  'alwaysOriginal',
] as const;
type ImageFitResolutionConfigKey =
  (typeof IMAGE_FIT_AND_RESOLUTION_CONFIG_FIELDS)[number];

export type ImageFitResolutionKey = ImageFitResolutionConfigKey | 'default';

type ImageFitAndResolutionOption = {
  id: ImageFitResolutionKey;
  label: string;
  description?: string;
  decorationLeft: ItemRowDecorationLeftProps;
  enabled: boolean;
};

/** Used so SelectField can reference the visible section title (SelectField does not forward aria-label to the trigger). */
const IMAGE_FIT_AND_RESOLUTION_TITLE_ID =
  'media-settings-image-fit-resolution-title';

const OPTIONS: ImageFitAndResolutionOption[] = [
  {
    id: 'default',
    label: 'Thumbnails (default)',
    description: 'Use thumbnails to optimize loading speed.',
    decorationLeft: {iconProps: {icon: 'layout-grid'}},
    // Thumbnails are only available on public cloud
    enabled: envIsPublicCloud,
  },
  {
    id: 'default',
    label: 'Default',
    description: 'Use the default image dimensions for the panel mode.',
    decorationLeft: {iconProps: {icon: 'layout-grid'}},
    enabled: envIsServerDeployment,
  },
  {
    id: 'alwaysOriginal',
    label: 'Original image',
    description: 'Load full-resolution images for detailed inspection.',
    decorationLeft: {iconProps: {icon: 'photo-picture'}},
    enabled: envIsPublicCloud,
  },
  {
    id: 'fitToDimension',
    label: 'Fit to available space',
    description: 'Resize full-resolution images to fit within the panel.',
    decorationLeft: {iconProps: {icon: 'pan-tool'}},
    enabled: true,
  },
  {
    id: 'actualSize',
    label: 'Original size',
    description: 'Load full-resolution images in their original dimensions.',
    decorationLeft: {iconProps: {icon: '1x'}},
    enabled: true,
  },
];

export const ImageFitAndResolution = () => {
  const {
    updateConfig,
    unsetConfigFields,
    settingsLevel,
    panelRef,
    configWithoutDefaults,
  } = useMediaSettingsContext();
  const mode = useMediaModeDisplay(settingsLevel, panelRef);
  const actualSize = useMediaActualSizeDisplay(settingsLevel, panelRef);
  const fitToDimension = useMediaFitToDimensionDisplay(settingsLevel, panelRef);
  const alwaysOriginal = useMediaAlwaysOriginalDisplay(settingsLevel, panelRef);
  // Unfortunately the settings are all mutually exclusive booleans instead of a single enum setting.
  // We need to find the first enabled config field to determine the selected option.
  const configFieldsForMode =
    getModeConfig(mode).imageFitAndResolutionConfigFields;
  const nonDefaultConfigFieldsForMode = configFieldsForMode.filter(
    field => field !== 'default'
  ) as ImageFitResolutionConfigKey[];
  const configValues: Record<ImageFitResolutionConfigKey, boolean | undefined> =
    {
      actualSize,
      fitToDimension,
      alwaysOriginal,
    };
  const enabledConfigField = nonDefaultConfigFieldsForMode.find(
    field => configValues[field] === true
  );
  const selectedOption =
    OPTIONS.find(option => enabledConfigField === option.id)?.id ?? 'default';
  const options = OPTIONS.filter(
    option => configFieldsForMode.includes(option.id) && option.enabled
  );
  // Dirty means set in this level's own raw config; the effective (cascaded)
  // values above may come from a parent level.
  const isSetAtLevel = nonDefaultConfigFieldsForMode.some(
    field => configWithoutDefaults[field] !== undefined
  );

  const unsetGroupAtLevel = () => {
    const fieldsSetAtLevel = nonDefaultConfigFieldsForMode.filter(
      field => configWithoutDefaults[field] !== undefined
    );
    if (fieldsSetAtLevel.length > 0) {
      unsetConfigFields(fieldsSetAtLevel);
    }
  };

  const onChange = (key: string | number | null) => {
    if (key == null) {
      return;
    }
    // Write the whole group so this level fully owns the choice: an explicit
    // `false` blocks a parent level's `true`, which unsetting cannot do
    // (unset means "inherit"). This is what lets e.g. section-level
    // "Thumbnails" override a workspace-level "Original image".
    const groupUpdate: Partial<Record<ImageFitResolutionConfigKey, boolean>> =
      {};
    for (const field of nonDefaultConfigFieldsForMode) {
      groupUpdate[field] = field === key;
    }
    updateConfig(groupUpdate);
  };

  return (
    <div className="flex flex-col gap-y-12">
      <SettingTitle
        id={IMAGE_FIT_AND_RESOLUTION_TITLE_ID}
        title="Image fit and resolution"
        description={`Choose how you would like to display images in the panel${envIsPublicCloud ? ' (we always load full-resolution images in the image full screen view)' : ''}.`}
        configField="actualSize"
        isDirty={isSetAtLevel}
        unsetConfigField={unsetGroupAtLevel}
      />
      <SelectField
        items={options}
        value={selectedOption}
        onChange={onChange}
        aria-label="Image fit and resolution"
        fullWidth
      />
    </div>
  );
};
